import { useEffect } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import usePrevious from '@src/quince-site-common/src/hooks/usePrevious'
import { trackPageViewForProduct } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { isSearchResult } from '@src/quince-site-common/src/services/algoliaService/utils'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'
import { FINDING_METHOD_AGGREGATE, getCurrentFindingMethod, setFindingMethod } from '@src/telemetry/findingMethod'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { isEqual } from '@src/utils/functions/utils'

export const useProductTracker = (
  product: Product,
  initialSelectedOptions: ProductVariantOption[],
  sectionData: TPDPSectionData,
  productProperties: ProductProperties,
) => {
  const previousProduct = usePrevious(product)
  useEffect(() => {
    const currentProductID = product.shopifyProductId
    const previousProductID = previousProduct?.shopifyProductId
    if (!isEqual(currentProductID, previousProductID)) {
      const applicableVariants = productHelper.getVariantsForOptions(product, initialSelectedOptions)
      const { shopifyVariantId, title, sku, price, options, id } = applicableVariants[0] ?? {}

      const variantColor = options?.filter(t => t.name === ProductOptionName.Color)[0]?.value ?? ''
      const variantsImages = sectionData.colorValueProductImagesMap[variantColor]
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_VIEW, {
        productId: product.shopifyProductId,
        isValentineProduct: product.isValentineProduct,
        internalProductId_color: product.id.toString() + '_' + (variantColor || ''),
        variantId: shopifyVariantId,
        variantTitle: title,
        sku,
        productTitle: product.title,
        price,
        productType: product.productTypeName,
        vendor: 'Quince',
        variantImageUrl: variantsImages?.[0]?.image?.url,
        division: product.classification?.division?.name,
        subDepartment: product.classification?.subDepartment?.name,
        department: product.classification?.department?.name,
        class: product.classification?.class?.name,
        subClass: product.classification?.subClass?.name,
        gender: product.gender,
        variantIds: applicableVariants.map(v => v.shopifyVariantId),
        internal_variant_id: id,
        internal_product_id: product.id,
        slug: product.slug,
        context: {
          findingMethodAggregate: currentFindingMethod?.findingMethodAggregate,
          findingMethodGranular: currentFindingMethod?.findingMethodGranular,
          findingMethodId: currentFindingMethod?.findingMethodId,
          productProperties,
        },
      })
      setFindingMethod[FINDING_METHOD_AGGREGATE.LANDING_PAGE]?.()
      if (!isSearchResult())
        trackPageViewForProduct({
          objectID: `${product.shopifyProductId}_${initialSelectedOptions[0]?.value}`,
        })
    }
  }, [product.id, product])
}
